import type { SeedThreeSpeciesPreset } from './seedThreeAssets.ts';

/** SeedThree presets selected for Gorski Kotar — no GUI/control imports. */
export const GORSKI_KOTAR_SPECIES: Record<string, SeedThreeSpeciesPreset> = {
  americanBeech: {
    name: 'American Beech',
    bark: 'american_beech_albedo.png',
    leaf: 'american_beech_single_albedo.png',
    foliage: {
      mode: 'leaves',
      clustersPerBranch: 3,
      clusterSize: 1.3,
      clusterSizeVar: 0.3,
      clusterQuads: 2,
      tint: 0xd6e6b0,
      leavesPerBranch: 7,
      cardCoverage: 1.5,
      cardRadialPlanes: 2,
      cardCrownUnderlay: true,
      cardCrownUnderlayLateralScale: 1.35,
      mobileNearTwigCollapse: true,
      size: 0.5,
      downAngle: 54,
      bend: 0,
      trunkClearRadius: 0.7,
    },
    params: {
      scale: 16,
      scaleV: 2,
      levels: 3,
      ratio: 0.03,
      ratioPower: 1.3,
      baseSize: 0.2,
      shape: 1,
      flare: 0.6,
      attractionUp: 0.45,
      baseSplits: 0,
      baseSplitAngle: 0,
      length: [1.0, 0.6, 0.16, 0.14],
      lengthV: [0.0, 0.16, 0.12, 0.1],
      taper: [1.0, 1.0, 1.0, 1.0],
      curveRes: [12, 7, 5, 3],
      curve: [6, 24, 26, 0],
      curveBack: [0, -14, 0, 0],
      curveV: [16, 62, 65, 58],
      downAngle: [0, 70, 60, 54],
      downAngleV: [0, 22, 24, 24],
      rotate: [0, 140, 140, 140],
      rotateV: [0, 32, 32, 32],
      branches: [0, 30, 22, 0],
      radialSegments: [12, 8, 6, 4],
    },
  },
  whiteOak: {
    name: 'White Oak',
    bark: 'white_oak_albedo.png',
    leaf: 'white_oak_single_albedo.png',
    foliage: {
      mode: 'leaves',
      clustersPerBranch: 3,
      clusterSize: 1.3,
      clusterSizeVar: 0.3,
      clusterQuads: 2,
      tint: 0xdfe8c8,
      leavesPerBranch: 14,
      cardCoverage: 1.5,
      cardRadialPlanes: 2,
      cardCrownUnderlay: true,
      cardCrownUnderlayLateralScale: 1.35,
      mobileNearTwigCollapse: true,
      size: 0.6,
      downAngle: 52,
      bend: 0,
    },
    params: {
      scale: 13,
      scaleV: 2,
      levels: 3,
      ratio: 0.035,
      ratioPower: 1.3,
      baseSize: 0.18,
      shape: 1,
      flare: 0.8,
      attractionUp: 0.7,
      baseSplits: 1,
      baseSplitAngle: 18,
      length: [1.0, 0.5, 0.42, 0.35],
      lengthV: [0.0, 0.12, 0.12, 0.1],
      taper: [1.0, 1.0, 1.0, 1.0],
      curveRes: [12, 7, 5, 3],
      curve: [6, 30, 35, 0],
      curveBack: [0, -20, 0, 0],
      curveV: [16, 80, 80, 70],
      downAngle: [0, 68, 55, 50],
      downAngleV: [0, 18, 20, 20],
      rotate: [0, 140, 140, 140],
      rotateV: [0, 30, 30, 30],
      branches: [0, 26, 14, 0],
      radialSegments: [14, 10, 6, 5],
    },
  },
  redMaple: {
    name: 'Red Maple',
    bark: 'red_maple_albedo.png',
    leaf: 'red_maple_single_albedo.png',
    foliage: {
      mode: 'leaves',
      clustersPerBranch: 3,
      clusterSize: 1.2,
      clusterSizeVar: 0.3,
      clusterQuads: 2,
      tint: 0xdce4b4,
      leavesPerBranch: 7,
      cardCoverage: 1.5,
      cardRadialPlanes: 2,
      cardCrownUnderlay: true,
      cardCrownUnderlayLateralScale: 1.3,
      mobileNearTwigCollapse: true,
      size: 0.55,
      downAngle: 50,
      bend: 0,
      trunkClearRadius: 0.85,
    },
    params: {
      scale: 15,
      scaleV: 2,
      levels: 3,
      ratio: 0.028,
      ratioPower: 1.3,
      baseSize: 0.18,
      shape: 1,
      flare: 0.45,
      attractionUp: 0.5,
      baseSplits: 0,
      baseSplitAngle: 0,
      length: [1.0, 0.5, 0.16, 0.14],
      lengthV: [0.0, 0.1, 0.08, 0.06],
      taper: [1.0, 1.0, 1.0, 1.0],
      curveRes: [12, 6, 4, 3],
      curve: [4, 18, 12, 0],
      curveBack: [0, 0, 0, 0],
      curveV: [8, 38, 42, 42],
      downAngle: [0, 48, 55, 58],
      downAngleV: [0, 12, 14, 14],
      rotate: [0, 137, 137, 137],
      rotateV: [0, 20, 20, 20],
      branches: [0, 34, 20, 0],
      radialSegments: [12, 8, 6, 4],
    },
  },
  sweetgum: {
    name: 'Sweetgum',
    bark: 'sweetgum_albedo.png',
    leaf: 'sweetgum_single_albedo.png',
    foliage: {
      mode: 'leaves',
      clustersPerBranch: 3,
      clusterSize: 1.25,
      clusterSizeVar: 0.3,
      clusterQuads: 2,
      tint: 0xcfe4ac,
      leavesPerBranch: 7,
      cardCoverage: 1.5,
      cardRadialPlanes: 2,
      cardCrownUnderlay: true,
      cardCrownUnderlayLateralScale: 1.28,
      mobileNearTwigCollapse: true,
      size: 0.55,
      downAngle: 55,
      bend: 0,
      trunkClearRadius: 0.75,
    },
    params: {
      scale: 16,
      scaleV: 2,
      levels: 3,
      ratio: 0.025,
      ratioPower: 1.3,
      baseSize: 0.12,
      shape: 0,
      flare: 0.4,
      attractionUp: 0.15,
      baseSplits: 0,
      baseSplitAngle: 0,
      length: [1.0, 0.45, 0.16, 0.14],
      lengthV: [0.0, 0.1, 0.08, 0.06],
      taper: [1.0, 1.0, 1.0, 1.0],
      curveRes: [12, 6, 4, 3],
      curve: [3, 14, 8, 0],
      curveBack: [0, 0, 0, 0],
      curveV: [8, 28, 32, 32],
      downAngle: [0, 55, 62, 66],
      downAngleV: [0, 12, 14, 14],
      rotate: [0, 137, 137, 137],
      rotateV: [0, 20, 20, 20],
      branches: [0, 40, 22, 0],
      radialSegments: [12, 8, 6, 4],
    },
  },
  douglasFir: {
    name: 'Douglas Fir',
    bark: 'douglas_fir_albedo.png',
    leaf: 'douglas_fir_needle_albedo.png',
    foliage: {
      mode: 'leaves',
      clustersPerBranch: 3,
      clusterSize: 1.1,
      clusterSizeVar: 0.3,
      clusterQuads: 2,
      tint: 0xc6d2b4,
      leavesPerBranch: 10,
      cardCrownUnderlay: true,
      cardCrownUnderlayLateralScale: 1.35,
      size: 0.6,
      downAngle: 65,
      bend: 0,
      startFrac: 0.15,
    },
    params: {
      scale: 34,
      scaleV: 3,
      levels: 3,
      ratio: 0.02,
      ratioPower: 1.4,
      baseSize: 0.12,
      shape: 0,
      flare: 0.5,
      attractionUp: 0.0,
      baseSplits: 0,
      baseSplitAngle: 0,
      length: [1.0, 0.34, 0.34, 0.28],
      lengthV: [0.0, 0.08, 0.1, 0.1],
      taper: [1.0, 1.0, 1.0, 1.0],
      curveRes: [14, 6, 4, 3],
      curve: [2, 16, 22, 0],
      curveBack: [0, 0, 0, 0],
      curveV: [4, 18, 24, 24],
      downAngle: [0, 84, 82, 78],
      downAngleV: [0, 10, 12, 12],
      rotate: [0, 137, 137, 137],
      rotateV: [0, 22, 22, 22],
      branches: [0, 52, 18, 0],
      radialSegments: [12, 6, 4, 3],
    },
  },
  loblolly: {
    name: 'Loblolly Pine',
    bark: 'loblolly_albedo.png',
    leaf: 'loblolly_needle_albedo.png',
    foliage: {
      mode: 'leaves',
      clustersPerBranch: 3,
      clusterSize: 1.2,
      clusterSizeVar: 0.3,
      clusterQuads: 2,
      tint: 0xccd6b8,
      leavesPerBranch: 8,
      cardCrownUnderlay: true,
      cardCrownUnderlayLateralScale: 1.35,
      size: 0.85,
      downAngle: 55,
      bend: 0,
      startFrac: 0.2,
    },
    params: {
      scale: 30,
      scaleV: 3,
      levels: 3,
      ratio: 0.018,
      ratioPower: 1.4,
      baseSize: 0.5,
      shape: 0,
      flare: 0.4,
      attractionUp: 0.15,
      baseSplits: 0,
      baseSplitAngle: 0,
      length: [1.0, 0.35, 0.34, 0.28],
      lengthV: [0.0, 0.1, 0.1, 0.1],
      taper: [1.0, 1.0, 1.0, 1.0],
      curveRes: [14, 6, 4, 3],
      curve: [2, 12, 14, 0],
      curveBack: [0, 0, 0, 0],
      curveV: [5, 22, 26, 26],
      downAngle: [0, 72, 68, 62],
      downAngleV: [0, 12, 14, 14],
      rotate: [0, 137, 137, 137],
      rotateV: [0, 24, 24, 24],
      branches: [0, 40, 16, 0],
      radialSegments: [12, 6, 4, 3],
    },
  },
  pine: {
    name: 'Ponderosa Pine',
    bark: 'pine_albedo.png',
    leaf: 'pine_needle_albedo.png',
    foliage: {
      mode: 'leaves',
      clustersPerBranch: 3,
      clusterSize: 1.1,
      clusterSizeVar: 0.3,
      clusterQuads: 2,
      tint: 0xcdd8c0,
      leavesPerBranch: 9,
      cardCrownUnderlay: true,
      cardCrownUnderlayLateralScale: 1.35,
      size: 0.7,
      downAngle: 62,
      bend: 0,
      startFrac: 0.15,
    },
    params: {
      scale: 30,
      scaleV: 3,
      levels: 3,
      ratio: 0.02,
      ratioPower: 1.4,
      baseSize: 0.12,
      shape: 0,
      flare: 0.5,
      attractionUp: 0.0,
      baseSplits: 0,
      baseSplitAngle: 0,
      length: [1.0, 0.34, 0.34, 0.28],
      lengthV: [0.0, 0.08, 0.1, 0.1],
      taper: [1.0, 1.0, 1.0, 1.0],
      curveRes: [14, 6, 4, 3],
      curve: [2, 12, 16, 0],
      curveBack: [0, 0, 0, 0],
      curveV: [4, 18, 24, 24],
      downAngle: [0, 82, 80, 74],
      downAngleV: [0, 10, 12, 12],
      rotate: [0, 137, 137, 137],
      rotateV: [0, 22, 22, 22],
      branches: [0, 46, 16, 0],
      radialSegments: [12, 6, 4, 3],
    },
  },
};
